import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { FloatingWhatsApp } from '../components/FloatingWhatsApp';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../mock';

export const Portfolio = () => {
  const navigate = useNavigate();
  const observerRef = useRef(null);
  const projects = mockData.portfolio;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = document.querySelectorAll('.observe-animation');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="relative min-h-screen bg-neutral-950 text-white overflow-hidden">
      <Navigation />
      <FloatingWhatsApp />

      <div className="pointer-events-none absolute inset-0">
        <div className="gradient-orb orb-1" />
        <div className="gradient-orb orb-2" />
        <div className="gradient-orb orb-3" />
        <div className="grain-overlay" />
      </div>

      <main className="relative max-w-6xl lg:max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-28 pb-20">
        <section className="space-y-6 observe-animation">
          <div className="flex items-center gap-3 text-sm text-white/70">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} /> Back
            </button>
            <span className="h-[1px] w-10 bg-white/15" />
            <Badge className="bg-white/10 text-white border-white/10">Portfolio</Badge>
          </div>
          <h1 className="text-4xl sm:text-5xl font-semibold">In Production</h1>
          <p className="text-white/60 max-w-2xl">
            A snapshot of live, production-ready builds delivered for clients.
          </p>
        </section>

        <section className="mt-12 grid gap-6 lg:grid-cols-2">
          {projects.map((project, idx) => (
            <div
              key={project.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/project/${project.id}`)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/project/${project.id}`)}
              className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl hover:border-white/20 transition-all duration-500 group observe-animation hover-card cursor-pointer"
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute -inset-10 bg-gradient-to-r from-neutral-500/10 via-neutral-400/10 to-white/5 blur-3xl" />
              </div>
              <Card className="bg-transparent border-none shadow-none">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex flex-col lg:flex-row gap-8 lg:items-center">
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center gap-3 text-white/60 text-sm uppercase tracking-[0.2em]">
                        <span className="h-[2px] w-8 bg-white/20" />
                        Project {String(idx + 1).padStart(2, '0')}
                        <span className="h-[2px] w-8 bg-white/20" />
                        <Badge className="bg-white/10 text-white border-white/10">{project.category}</Badge>
                      </div>
                      <h3 className="text-2xl font-semibold text-white">{project.title}</h3>
                      <p className="text-white/70 max-w-2xl">{project.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {project.features.slice(0, 4).map((feature) => (
                          <span
                            key={feature}
                            className="text-xs px-3 py-1 rounded-full bg-white/10 text-white/80 border border-white/10"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-white/60">
                        <span className="h-2 w-2 rounded-full bg-neutral-200" />
                        In production
                      </div>
                    </div>

                    <div className="relative w-full lg:w-72 xl:w-80 hover-card">
                      <div className="absolute -inset-4 bg-gradient-to-br from-neutral-500/25 via-neutral-400/20 to-neutral-700/25 rounded-[22px] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black/30 shadow-2xl">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                        <div className="absolute top-3 right-3">
                          <ArrowUpRight size={18} className="text-white/70 arrow-float" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </section>
      </main>

      <style>{`
        @keyframes floatOne {
          from { transform: translate3d(0, 0, 0) scale(1); }
          to { transform: translate3d(6%, -4%, 0) scale(1.05); }
        }
        @keyframes floatTwo {
          from { transform: translate3d(0, 0, 0) scale(1); }
          to { transform: translate3d(-8%, 6%, 0) scale(1.08); }
        }
        @keyframes floatThree {
          from { transform: translate3d(0, 0, 0) scale(1); }
          to { transform: translate3d(4%, 8%, 0) scale(1.06); }
        }
        .gradient-orb {
          position: absolute;
          width: 60vw;
          height: 60vw;
          filter: blur(140px);
          opacity: 0.4;
          mix-blend-mode: screen;
        }
        .orb-1 {
          top: -18vh;
          left: -12vw;
          background: radial-gradient(circle at 30% 30%, rgba(196, 196, 196, 0.8), transparent 55%);
          animation: floatOne 18s ease-in-out infinite alternate;
        }
        .orb-2 {
          top: 14vh;
          right: -18vw;
          background: radial-gradient(circle at 40% 40%, rgba(170, 170, 170, 0.65), transparent 60%);
          animation: floatTwo 22s ease-in-out infinite alternate;
        }
        .orb-3 {
          bottom: -14vh;
          left: 18vw;
          background: radial-gradient(circle at 50% 50%, rgba(120, 120, 120, 0.5), transparent 60%);
          animation: floatThree 26s ease-in-out infinite alternate;
        }
        .grain-overlay {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 0);
          background-size: 4px 4px;
          opacity: 0.32;
          mix-blend-mode: soft-light;
        }
      `}</style>
    </div>
  );
};
